import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface FunnelStep {
  event: string;
  count: number;
  conversionFromStart: number;
}

/** Org-level Conversion Funnel page with a top project selector. Supports both
 * page-path funnels (SPA-friendly) and custom-event funnels. */
export function FunnelPage() {
  const t = useT().analytics;
  const theme = useTheme();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { projectId: activeProject } = useActiveProject(orgId);

  const [steps, setSteps] = useState<string[]>([]);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [mode, setMode] = useState<'event' | 'page'>('page');

  // Reset the built funnel when the project changes.
  useEffect(() => {
    setSteps([]);
    setFunnel([]);
  }, [activeProject]);

  const events = useQuery({
    enabled: !!orgId && !!activeProject,
    queryKey: ['events', orgId, activeProject],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${activeProject}/analytics/events`)).data.data as {
        event: string;
        count: number;
      }[],
  });
  const pages = useQuery({
    enabled: !!orgId && !!activeProject,
    queryKey: ['pages', orgId, activeProject],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${activeProject}/analytics/pages`)).data.data as {
        path: string;
        count: number;
      }[],
  });

  const runFunnel = async () => {
    if (steps.length < 2) return;
    const to = new Date().toISOString();
    const from = new Date(Date.now() - 90 * 86_400_000).toISOString();
    const res = await api.post(`/v1/orgs/${orgId}/projects/${activeProject}/analytics/funnel`, {
      steps,
      from,
      to,
      mode,
    });
    setFunnel(res.data.data);
  };

  const src =
    mode === 'page'
      ? (pages.data ?? []).map((p) => ({ label: p.path, count: p.count }))
      : (events.data ?? []).map((e) => ({ label: e.event, count: e.count }));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{t.funnel.title}</Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => {
            if (v) {
              setMode(v as 'event' | 'page');
              setSteps([]);
              setFunnel([]);
            }
          }}
        >
          <ToggleButton value="page">{t.funnel.pages}</ToggleButton>
          <ToggleButton value="event">{t.funnel.customEvents}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
        <Autocomplete
          multiple
          freeSolo
          size="small"
          fullWidth
          options={src.map((o) => o.label)}
          value={steps}
          onChange={(_, v) => setSteps(v as string[])}
          renderOption={(props, opt) => {
            const count = src.find((o) => o.label === opt)?.count;
            return (
              <li {...props} key={opt}>
                <Box sx={{ flex: 1, fontFamily: mode === 'page' ? 'monospace' : undefined }}>{opt}</Box>
                {count != null && (
                  <Typography variant="caption" color="text.secondary">
                    {count} session{count === 1 ? '' : 's'}
                  </Typography>
                )}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                src.length === 0
                  ? mode === 'page'
                    ? t.funnel.noPages
                    : t.funnel.noEvents
                  : mode === 'page'
                    ? t.funnel.stepsPages
                    : t.funnel.stepsEvents
              }
              placeholder={mode === 'page' ? 'e.g. / → /pricing → /signup' : 'e.g. add_to_cart → begin_checkout → purchase'}
            />
          )}
        />
        <Button variant="contained" onClick={runFunnel} disabled={steps.length < 2}>
          {t.funnel.run}
        </Button>
      </Stack>

      {funnel.length > 0 && (
        <Paper sx={{ p: 2, height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="event" stroke={theme.palette.text.secondary} />
              <YAxis stroke={theme.palette.text.secondary} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: theme.palette.action.hover }}
                contentStyle={{
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  color: theme.palette.text.primary,
                  boxShadow: theme.shadows[3],
                }}
                formatter={(v: number, _n, p) => [
                  t.funnel.tooltip(v, Math.round((p.payload.conversionFromStart ?? 0) * 100)),
                  'count',
                ]}
              />
              <Bar dataKey="count" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
}
